import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Building2, LineChart, Briefcase, FileSearch } from 'lucide-react';
import TerrainBackground from '@/components/assessment/TerrainBackground';
import { usePortal } from '@/contexts/PortalContext';

// About / methodology page. The shape is fixed; the founder-story copy is
// intentionally a draft that the founder (Jasmeet) should rewrite in her own
// voice. The research anchors are factual and match what the assessment
// engine actually does.

const researchAnchors = [
  {
    icon: GraduationCap,
    name: 'Holland\'s RIASEC framework',
    body:
      'John Holland\'s theory of vocational personalities (1959) is the most-validated career framework in vocational psychology. Six interest categories: Realistic, Investigative, Artistic, Social, Enterprising, Conventional. TruePath maps every assessment answer to a RIASEC code, then ranks paths by interest fit. This is the same framework U.S. federal employment counselors use.',
  },
  {
    icon: Building2,
    name: 'O*NET (U.S. Department of Labor)',
    body:
      'O*NET is the federal occupational database, updated continuously. It catalogs 900+ occupations with skills, tasks, growth projections, and wage data. TruePath does not invent careers. Every recommended path is a real occupation pulled from O*NET, with O*NET\'s own skill requirements and outlook attached.',
  },
  {
    icon: LineChart,
    name: 'Bureau of Labor Statistics (BLS) projections',
    body:
      'BLS publishes 10-year employment projections every two years. The current edition covers 2024-2034. Every path includes its BLS growth rate and median wage. Paths that BLS projects will shrink are flagged.',
  },
  {
    icon: Briefcase,
    name: 'World Economic Forum Future of Jobs',
    body:
      'The WEF Future of Jobs Report identifies tasks and roles most exposed to AI and automation. We use it to compute an AI Resilience Score on every recommended path so students see not just where the work is, but where it is going.',
  },
  {
    icon: FileSearch,
    name: 'McKinsey Global Institute automation modeling',
    body:
      'McKinsey\'s research on which tasks AI is most likely to redefine informs the path filtering. Paths where McKinsey models 70%+ task automation by 2030 are deprioritized for first-time learners.',
  },
];

const PortalAbout = () => {
  const { config, basePath, label } = usePortal();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const fadeUp = prefersReducedMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.3 } }
    : { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

  return (
    <div className="min-h-screen bg-journey-atmosphere relative overflow-hidden">
      <TerrainBackground />
      <div className="noise-overlay" />

      <main id="main-content" className="relative z-10">
        {/* Header */}
        <div className="px-6 pt-10 pb-4 max-w-3xl mx-auto">
          <button
            onClick={() => navigate(basePath)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to {label} edition
          </button>
        </div>

        {/* Hero */}
        <section className="px-6 pt-6 pb-16">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <motion.p
              {...fadeUp}
              transition={{ delay: 0.1 }}
              className="text-xs font-semibold tracking-[0.18em] uppercase text-primary/80"
            >
              About TruePath
            </motion.p>
            <motion.h1
              {...fadeUp}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-4xl md:text-5xl font-display font-bold text-foreground leading-tight"
            >
              Built so the next generation doesn’t have to{' '}
              <span className="text-gradient-teal">guess</span> their way into work.
            </motion.h1>
          </div>
        </section>

        {/* How it works — research grounding */}
        <section className="px-6 pb-20" aria-labelledby="research-heading">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center space-y-3 mb-10"
            >
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-primary/80">
                Methodology
              </p>
              <h2 id="research-heading" className="text-2xl md:text-3xl font-display font-bold text-foreground">
                What is actually inside the assessment.
              </h2>
              <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Every answer you give is mapped against five public research frameworks. Nothing here is invented; it is assembled.
              </p>
            </motion.div>

            <div className="space-y-3">
              {researchAnchors.map((anchor, i) => (
                <motion.div
                  key={anchor.name}
                  initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="glass-card p-5 md:p-6 flex gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <anchor.icon className="w-5 h-5 text-primary" aria-hidden="true" />
                  </div>
                  <div className="space-y-1.5 min-w-0">
                    <h3 className="font-display font-bold text-foreground text-base">{anchor.name}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{anchor.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why RIASEC — addresses the question a counselor or researcher
            will ask: why this framework and not Multiple Intelligences,
            StrengthsFinder, MBTI, etc. Confident, not defensive. */}
        <section className="px-6 pb-20" aria-labelledby="why-riasec-heading">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 space-y-4"
            >
              <h2 id="why-riasec-heading" className="font-display font-bold text-xl text-foreground">
                Why RIASEC, and not the framework you may have seen elsewhere.
              </h2>
              <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                <p>
                  Many career and learning tools are built on Multiple Intelligences, MBTI, or proprietary type systems. Those frameworks are popular and easy to market. They are also weakly supported in the research.
                </p>
                <p>
                  RIASEC is different. John Holland&apos;s model of vocational interests has been tested and replicated for over 65 years, across cultures, languages, and large samples. It is the framework behind the U.S. Department of Labor&apos;s O*NET, the Strong Interest Inventory, and most professional career counseling. It was built for one purpose: predicting which kinds of work a person will find engaging and stay in.
                </p>
                <p>
                  That is exactly the question TruePath answers, so we use the instrument designed for it, not a borrowed one. We pair it with work values and live labor-market data, because interests alone do not decide a career. The result is a more complete picture than a single type ever gives.
                </p>
                <p className="text-xs text-muted-foreground/70 italic">
                  We translate the six RIASEC codes into plain language (Builder, Analyst, Creator, Helper, Leader, Organizer) so the experience is human. The rigorous engine underneath does not change.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Honest limits */}
        <section className="px-6 pb-20" aria-labelledby="honesty-heading">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 space-y-4 border-accent/20"
            >
              <h2 id="honesty-heading" className="font-display font-bold text-xl text-foreground">
                What TruePath is not.
              </h2>
              <ul className="space-y-2.5 text-sm text-muted-foreground leading-relaxed">
                <li className="flex gap-2"><span className="text-accent shrink-0">•</span>Not a personality test. It will not tell you that you are an INFJ.</li>
                <li className="flex gap-2"><span className="text-accent shrink-0">•</span>Not a destiny machine. It surfaces directions that fit who you are, not a single “right” answer.</li>
                <li className="flex gap-2"><span className="text-accent shrink-0">•</span>Not a replacement for your counselor. It is a faster, more data-rich starting point for the conversations you would have with them.</li>
                <li className="flex gap-2"><span className="text-accent shrink-0">•</span>Not selling your data. {config.label} edition is FERPA-compliant; the privacy policy is plain English.</li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* CTA back to assessment */}
        <section className="px-6 pb-24">
          <div className="max-w-xl mx-auto text-center space-y-5">
            <h2 className="text-2xl font-display font-bold text-foreground">Ready to start?</h2>
            <button
              onClick={() => navigate(basePath)}
              className="btn-primary text-base px-8 py-4 rounded-2xl glow-teal"
            >
              Back to the {label} assessment
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PortalAbout;
